<template>
  <v-main class="subscription-main">
    <v-container class="pa-6">
      <!-- Header -->
      <v-card class="modern-card mb-6" elevation="1">
        <v-card-text class="pa-6">
          <div class="d-flex align-center mb-4">
            <v-icon class="me-3" color="primary" size="large">mdi-credit-card</v-icon>
            <div>
              <h1 class="text-h4 font-weight-bold">Subscription Management</h1>
              <p class="text-body-1 text-medium-emphasis">Manage your billing and subscription settings</p>
            </div>
          </div>
        </v-card-text>
      </v-card>

      <!-- Current Subscription Status -->
      <v-card class="modern-card mb-6" elevation="1">
        <v-card-title class="card-header">
          <v-icon class="me-3" color="success">mdi-check-circle</v-icon>
          Current Plan
        </v-card-title>
        <v-card-text class="pa-6">
          <v-row v-if="subscription">
            <v-col cols="12" md="8">
              <div class="d-flex align-center mb-3">
                <v-avatar :color="getPlanColor(subscription.plan)" size="48" class="me-4">
                  <v-icon color="white" size="large">
                    {{ getPlanIcon(subscription.plan) }}
                  </v-icon>
                </v-avatar>
                <div>
                  <h3 class="text-h5 font-weight-bold">{{ subscription.plan.name }}</h3>
                  <p class="text-body-1 text-medium-emphasis">{{ subscription.plan.description }}</p>
                </div>
              </div>
              <div class="plan-details">
                <v-chip
                    :color="getStatusColor(subscription.status)"
                    variant="flat"
                    class="me-3 mb-2"
                >
                  {{ subscription.status }}
                </v-chip>
                <div class="text-body-2">
                  <div><strong>Price:</strong> ${{ subscription.plan.price }}/{{ subscription.plan.interval }}</div>
                  <div><strong>Next Billing:</strong> {{ formatDate(subscription.current_period_end) }}</div>
                  <div v-if="subscription.cancel_at_period_end">
                    <strong>Status:</strong> <span
                      class="text-warning">Cancels on {{ formatDate(subscription.current_period_end) }}</span>
                  </div>
                </div>
              </div>
            </v-col>
            <v-col cols="12" md="4">
              <div class="d-flex flex-column gap-3">
                <v-btn
                    color="primary"
                    variant="elevated"
                    @click="openCustomerPortal"
                    :loading="loadingPortal"
                    class="modern-btn"
                >
                  <v-icon class="me-2">mdi-cog</v-icon>
                  Manage Billing
                </v-btn>
                <v-btn
                    color="error"
                    variant="outlined"
                    @click="confirmCancel"
                    v-if="!subscription.cancel_at_period_end"
                    class="modern-btn"
                >
                  <v-icon class="me-2">mdi-cancel</v-icon>
                  Cancel Subscription
                </v-btn>
                <v-btn
                    color="success"
                    variant="outlined"
                    @click="reactivateSubscription"
                    v-if="subscription.cancel_at_period_end"
                    class="modern-btn"
                >
                  <v-icon class="me-2">mdi-refresh</v-icon>
                  Reactivate
                </v-btn>
              </div>
            </v-col>
          </v-row>
          <div v-else-if="loading" class="text-center py-8">
            <v-progress-circular indeterminate color="primary" size="64"></v-progress-circular>
            <p class="text-body-1 mt-4">Loading subscription details...</p>
          </div>
          <div v-else class="text-center py-8">
            <v-icon size="64" color="grey-lighten-1" class="mb-4">mdi-credit-card-off</v-icon>
            <h3 class="text-h5 mb-2">No Active Subscription</h3>
            <p class="text-body-1 text-medium-emphasis mb-4">You don't have an active subscription</p>
            <v-btn color="primary" variant="elevated" @click="showPlans = true" class="modern-btn">
              <v-icon class="me-2">mdi-plus</v-icon>
              Choose a Plan
            </v-btn>
          </div>
        </v-card-text>
      </v-card>

      <!-- Available Plans -->
      <v-card class="modern-card mb-6" elevation="1" v-if="showPlans || !subscription">
        <v-card-title class="card-header">
          <v-icon class="me-3" color="info">mdi-package-variant</v-icon>
          Available Plans
        </v-card-title>
        <v-card-text class="pa-6">
          <v-row>
            <v-col
                cols="12"
                md="4"
                v-for="plan in availablePlans"
                :key="plan.id"
                class="mb-4"
            >
              <v-card
                  class="plan-card h-100"
                  :class="{ 'selected-plan': selectedPlan === plan.id }"
                  elevation="2"
                  @click="selectedPlan = plan.id"
              >
                <v-card-text class="pa-6 text-center">
                  <v-avatar :color="getPlanColor(plan)" size="64" class="mb-4">
                    <v-icon color="white" size="large">
                      {{ getPlanIcon(plan) }}
                    </v-icon>
                  </v-avatar>
                  <h3 class="text-h5 font-weight-bold mb-2">{{ plan.name }}</h3>
                  <p class="text-body-1 text-medium-emphasis mb-4">{{ plan.description }}</p>
                  <div class="price-display mb-4">
                    <span class="text-h3 font-weight-bold">${{ plan.price }}</span>
                    <span class="text-body-1">/{{ plan.interval }}</span>
                  </div>
                  <v-list density="compact" class="text-left">
                    <v-list-item
                        v-for="feature in plan.features"
                        :key="feature"
                        class="px-0"
                    >
                      <template v-slot:prepend>
                        <v-icon color="success" size="small">mdi-check</v-icon>
                      </template>
                      <v-list-item-title class="text-body-2">{{ feature }}</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-card-text>
                <v-card-actions class="pa-4">
                  <v-btn
                      color="primary"
                      variant="elevated"
                      block
                      @click="subscribeToPlan(plan.id)"
                      :loading="loadingSubscription"
                      class="modern-btn"
                  >
                    {{ subscription && subscription.plan.id === plan.id ? 'Current Plan' : 'Select Plan' }}
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <!-- Billing History -->
      <v-card class="modern-card" elevation="1" v-if="subscription">
        <v-card-title class="card-header">
          <v-icon class="me-3" color="info">mdi-history</v-icon>
          Billing History
        </v-card-title>
        <v-card-text class="pa-6">
          <v-data-table
              :headers="billingHeaders"
              :items="billingHistory"
              :loading="loadingBilling"
              class="modern-table"
          >
            <!--            <template v-slot:item.amount="{ item }">-->
            <!--              <span class="font-weight-medium">${{ (item.amount / 100).toFixed(2) }}</span>-->
            <!--            </template>-->
            <!--            <template v-slot:item.status="{ item }">-->
            <!--              <v-chip -->
            <!--                :color="getPaymentStatusColor(item.status)" -->
            <!--                variant="flat" -->
            <!--                size="small"-->
            <!--              >-->
            <!--                {{ item.status }}-->
            <!--              </v-chip>-->
            <!--            </template>-->
            <!--            <template v-slot:item.date="{ item }">-->
            <!--              {{ formatDate(item.created) }}-->
            <!--            </template>-->
            <!--            <template v-slot:item.invoice="{ item }">-->
            <!--              <v-btn -->
            <!--                size="small" -->
            <!--                variant="outlined" -->
            <!--                @click="downloadInvoice(item.invoice_url)"-->
            <!--                class="modern-btn"-->
            <!--              >-->
            <!--                <v-icon size="small" class="me-1">mdi-download</v-icon>-->
            <!--                Download-->
            <!--              </v-btn>-->
            <!--            </template>-->
          </v-data-table>
        </v-card-text>
      </v-card>

      <!-- Cancel Confirmation Dialog -->
      <v-dialog v-model="showCancelDialog" max-width="500">
        <v-card>
          <v-card-title class="text-h5">Cancel Subscription</v-card-title>
          <v-card-text>
            <p>Are you sure you want to cancel your subscription?</p>
            <p class="text-body-2 text-medium-emphasis">
              You'll continue to have access until {{ formatDate(subscription?.current_period_end) }}.
            </p>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn @click="showCancelDialog = false" class="modern-btn">Keep Subscription</v-btn>
            <v-btn
                color="error"
                variant="elevated"
                @click="cancelSubscription"
                :loading="loadingCancel"
                class="modern-btn"
            >
              Cancel Subscription
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import api from '../api'

export default {
  name: 'SubscriptionManagement',
  data() {
    return {
      subscription: null,
      availablePlans: [],
      billingHistory: [],
      selectedPlan: null,
      showPlans: false,
      showCancelDialog: false,
      loading: true,
      loadingPortal: false,
      loadingSubscription: false,
      loadingBilling: false,
      loadingCancel: false,
      billingHeaders: [
        {title: 'Date', key: 'date', sortable: true},
        {title: 'Description', key: 'description', sortable: true},
        {title: 'Amount', key: 'amount', sortable: true},
        {title: 'Status', key: 'status', sortable: true},
        {title: 'Invoice', key: 'invoice', sortable: false}
      ]
    }
  },
  created() {
    this.fetchSubscriptionData()
  },
  methods: {
    async fetchSubscriptionData() {
      this.loading = true
      try {
        await Promise.all([
          this.fetchSubscription(),
          this.fetchAvailablePlans(),
          this.fetchBillingHistory()
        ])
      } catch (error) {
        console.error('Error fetching subscription data:', error)
        this.$toast.open({message: 'Error loading subscription data', type: 'error'})
      } finally {
        this.loading = false
      }
    },
    async fetchSubscription() {
      try {
        const subscription = await api.subscription.get()
        this.subscription = subscription
      } catch (error) {
        console.error('Error fetching subscription:', error)
      }
    },
    async fetchAvailablePlans() {
      try {
        const plans = await api.subscription.getPlans()
        this.availablePlans = plans
      } catch (error) {
        console.error('Error fetching plans:', error)
      }
    },
    async fetchBillingHistory() {
      this.loadingBilling = true
      try {
        const history = await api.subscription.getBillingHistory()
        this.billingHistory = history
      } catch (error) {
        console.error('Error fetching billing history:', error)
      } finally {
        this.loadingBilling = false
      }
    },
    async openCustomerPortal() {
      this.loadingPortal = true
      try {
        const portalUrl = await api.subscription.createPortalSession()
        window.open(portalUrl, '_blank')
      } catch (error) {
        console.error('Error opening customer portal:', error)
        this.$toast.open({message: 'Error opening billing portal', type: 'error'})
      } finally {
        this.loadingPortal = false
      }
    },
    async subscribeToPlan(planId) {
      this.loadingSubscription = true
      try {
        const checkoutUrl = await api.subscription.createCheckoutSession(planId)
        window.location.href = checkoutUrl
      } catch (error) {
        console.error('Error creating checkout session:', error)
        this.$toast.open({message: 'Error starting subscription', type: 'error'})
      } finally {
        this.loadingSubscription = false
      }
    },
    async cancelSubscription() {
      this.loadingCancel = true
      try {
        await api.subscription.cancel()
        this.$toast.open({message: 'Subscription cancelled', type: 'success'})
        this.showCancelDialog = false
        await this.fetchSubscription()
      } catch (error) {
        console.error('Error cancelling subscription:', error)
        this.$toast.open({message: 'Error cancelling subscription', type: 'error'})
      } finally {
        this.loadingCancel = false
      }
    },
    async reactivateSubscription() {
      try {
        await api.subscription.reactivate()
        this.$toast.open({message: 'Subscription reactivated', type: 'success'})
        await this.fetchSubscription()
      } catch (error) {
        console.error('Error reactivating subscription:', error)
        this.$toast.open({message: 'Error reactivating subscription', type: 'error'})
      }
    },
    downloadInvoice(invoiceUrl) {
      window.open(invoiceUrl, '_blank')
    },
    confirmCancel() {
      this.showCancelDialog = true
    },
    getPlanColor(plan) {
      const colors = {
        'free': 'grey',
        'basic': 'blue',
        'pro': 'purple',
        'enterprise': 'orange'
      }
      return colors[plan?.id] || 'primary'
    },
    getPlanIcon(plan) {
      const icons = {
        'free': 'mdi-account',
        'basic': 'mdi-star',
        'pro': 'mdi-crown',
        'enterprise': 'mdi-domain'
      }
      return icons[plan?.id] || 'mdi-package'
    },
    getStatusColor(status) {
      const colors = {
        'active': 'success',
        'cancelled': 'error',
        'past_due': 'warning',
        'incomplete': 'warning'
      }
      return colors[status] || 'grey'
    },
    getPaymentStatusColor(status) {
      const colors = {
        'paid': 'success',
        'pending': 'warning',
        'failed': 'error'
      }
      return colors[status] || 'grey'
    },
    formatDate(timestamp) {
      return new Date(timestamp * 1000).toLocaleDateString()
    }
  }
}
</script>

<style scoped>
.subscription-main {
  background: linear-gradient(135deg, var(--surface-sunken) 0%, var(--surface-sunken-end) 100%);
  min-height: 100vh;
}

.modern-card {
  border-radius: var(--radius-md) !important;
  transition: all 0.3s ease;
  border: 1px solid var(--border-subtle);
}

.modern-card:hover {
  transform: none;
  box-shadow: var(--shadow-md) !important;
}

.card-header {
  background: linear-gradient(135deg, var(--surface-sunken) 0%, var(--surface-sunken-end) 100%);
  border-bottom: 1px solid var(--border-subtle);
  font-weight: 600;
  padding: var(--space-5) var(--space-6);
}

.modern-btn {
  border-radius: var(--radius-none) !important;
  text-transform: none !important;
  font-weight: 500 !important;
  letter-spacing: 0.5px !important;
  transition: all 0.2s ease;
}

.plan-card {
  border-radius: var(--radius-md) !important;
  transition: all 0.3s ease;
  cursor: pointer;
  border: 2px solid transparent;
}

.plan-card:hover {
  transform: translateY(-4px);
  box-shadow: var(--shadow-lg) !important;
}

.selected-plan {
  border-color: #1976d2 !important;
  box-shadow: 0 4px 12px rgba(25, 118, 210, 0.2) !important;
}

.price-display {
  color: #1976d2;
}

.modern-table {
  border-radius: var(--radius-sm) !important;
}

.modern-table .v-data-table__wrapper {
  border-radius: var(--radius-sm) !important;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .plan-card {
    margin-bottom: var(--space-4);
  }

  .card-header {
    padding: var(--space-4) var(--space-5);
  }
}
</style>


